<template>
  <div id="super-admin-subscription-base" class="flex">
    <div>
      <div
        class="sticky top-0 z-50 h-10 flex items-center flex-shrink-0 border-b border-slate-400 bg-pure-white"
        :style="{ width: 'calc(100vw - 0.5rem)' }"
      >
        <BlBreadcrumbs class="sticky left-0 px-6" :breadcrumbs="breadcrumbs" />
      </div>

      <div class="py-6 flex justify-center">
        <div v-if="pageLoading" class="w-full h-128 grid place-items-center">
          <div class="blum-loader"></div>
        </div>
        <div
          v-else-if="subscription"
          class="max-w-180 w-full flex flex-col gap-5"
        >
          <div class="w-full bg-pure-white rounded-2xl">
            <div class="py-6 px-6">
              <div class="flex items-center mb-4">
                <BlTextTitle4 text="Subscription details" />
              </div>
              <div class="grid grid-cols-6 gap-3">
                <BlTextBody2
                  text="Subscription name"
                  class="col-span-2 text-slate-700"
                />
                <BlTextBody2
                  :text="subscription.name"
                  class="col-span-4 text-slate-1000"
                />
                <BlTextBody2 text="Status" class="col-span-2 text-slate-700" />
                <div class="flex col-span-4">
                  <BlChip :chip="subscription.status" dense />
                </div>
                <BlTextBody2
                  text="Subscription start date"
                  class="col-span-2 text-slate-700"
                />
                <BlTextBody2
                  :text="displayDate({ date: subscription.start_date })"
                  class="col-span-4 text-slate-1000"
                />
                <BlTextBody2
                  text="Subscription expiry date"
                  class="col-span-2 text-slate-700"
                />
                <BlTextBody2
                  :text="displayDate({ date: subscription.expiration_date })"
                  class="col-span-4 text-slate-1000"
                />
                <BlTextBody2
                  v-if="subscription.type === 'Subscription::Custom'"
                  text="Total amount due"
                  class="col-span-2 text-slate-700"
                />
                <div
                  v-if="subscription.type === 'Subscription::Custom'"
                  class="col-span-4 flex gap-1"
                >
                  <BlTextBody2
                    :text="subscription.currency_sym"
                    class="text-slate-1000"
                  />
                  <BlTextBody2
                    :text="priceFormats(subscription.amount)"
                    class="text-slate-1000"
                  />
                </div>
                <BlTextBody2
                  text="Number of seats"
                  class="col-span-2 text-slate-700"
                />
                <BlTextBody2
                  :text="`${subscription.number_of_seats} seat(s)`"
                  class="col-span-4 text-slate-1000"
                />
                <BlTextBody2 text="Notes" class="col-span-2 text-slate-700" />
                <BlTextBody2
                  :text="subscription.notes || '-'"
                  class="col-span-4 text-slate-1000 whitespace-pre-line"
                />
                <BlTextBody2
                  v-if="subscription.status === 'inactive'"
                  text="Subscription cancellation date"
                  class="col-span-2 text-slate-700"
                />
                <BlTextBody2
                  v-if="subscription.status === 'inactive'"
                  :text="
                    displayDate({
                      date: subscription.cancelled_at,
                      format: 'DD/MM/YYYY'
                    })
                  "
                  class="col-span-4 text-slate-1000"
                />
                <BlTextBody2
                  v-if="subscription.status === 'inactive'"
                  text="Cancelled by"
                  class="col-span-2 text-slate-700"
                />
                <BlTextBody2
                  v-if="subscription.status === 'inactive'"
                  :text="
                    (subscription.cancellation_user &&
                      subscription.cancellation_user.name) ||
                      '-'
                  "
                  class="col-span-4 text-slate-1000"
                />
                <BlTextBody2
                  v-if="subscription.status === 'inactive'"
                  text="Cancellation notes"
                  class="col-span-2 text-slate-700"
                />
                <BlTextBody2
                  v-if="subscription.status === 'inactive'"
                  :text="subscription.cancellation_reason || '-'"
                  class="col-span-4 text-slate-1000 whitespace-pre-line"
                />
              </div>
            </div>
            <div
              class="py-4 px-6 flex items-center justify-between border-t border-slate-400"
            >
              <BlInputButton
                type="outlined-error"
                :label="
                  subscription.status === 'inactive'
                    ? 'Subscription cancelled'
                    : 'Cancel subscription'
                "
                :disabled="subscription.status === 'inactive'"
                @clicked="cancelOpened = true"
              />
              <div class="flex items-center gap-4">
                <BlInputButton
                  type="outlined"
                  label="Edit subscription"
                  @clicked="editOpened = true"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <EditSubscriptionModal
      v-if="editOpened"
      :subscription="subscription"
      @close-modal="editOpened = false"
    />

    <BlBackdrop
      v-if="cancelOpened"
      :title="`Cancel subscription`"
      :label="`Cancel subscription`"
      :submitLoading="submitLoading"
      maxWidth="40rem"
      @backdrop-close="cancelOpened = false"
      @backdrop-action="onCancel"
    >
      <div class="px-8 flex flex-col gap-4">
        <div>
          <BlTextBody2 text="You are about to cancel the subscription for" />
          <BlTextBody2 :text="`${subscription.name}`" class="font-bold" />
          <BlTextBody2 text="By canceling the subscription," />
          <BlTextBody2 text="the center will be deactivated immediately," />
          <BlTextBody2
            text="and all users will no longer be able to access the center."
          />
        </div>
        <BlInputText :input="cancellationReason" @changed="inputHandler" />
      </div>
    </BlBackdrop>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { displayDate, displayPrice } from "@/plugins/helper.js";

import EditSubscriptionModal from "./EditSubscriptionModal.vue";

import BlTextTitle3 from "../../../BlTextTitle3.vue";
import BlTextTitle4 from "../../../BlTextTitle4.vue";
import BlTextBody2 from "../../../BlTextBody2.vue";
import BlBreadcrumbs from "../../../BlBreadcrumbs.vue";
import BlInputButton from "../../../BlInputButton.vue";
import BlInputText from "../../../BlInputText.vue";
import BlChip from "../../../BlChip.vue";
import BlBackdrop from "../../../BlBackdrop.vue";

export default {
  name: "Subscription",
  components: {
    EditSubscriptionModal,
    //
    BlTextTitle3,
    BlTextTitle4,
    BlTextBody2,
    BlBreadcrumbs,
    BlInputButton,
    BlInputText,
    BlChip,
    BlBackdrop
  },
  data() {
    return {
      pageLoading: true,
      submitLoading: false,
      editOpened: false,
      cancelOpened: false,
      cancellationReason: {
        label: "Please add a note for this cancellation (optional):",
        placeholder: "Type reason",
        textarea: true,
        model: ""
      }
    };
  },
  computed: {
    ...mapGetters(["currentUser", "center", "subscription"]),
    breadcrumbs() {
      const centerId = Number(this.$route.params.center_id);
      const centerLabel =
        (this.center && this.center.id) !== centerId
          ? "Center name"
          : this.center.name;
      const subscriptionId = Number(this.$route.params.id);
      const subscriptionLabel =
        (this.subscription && this.subscription.id) !== subscriptionId
          ? "Subscription name"
          : this.subscription.name;
      return [
        { label: "Centers management", target: `/super-admin/center` },
        {
          label: centerLabel,
          target: `/super-admin/center/${centerId}/subscription`
        },
        {
          label: subscriptionLabel,
          target: `/super-admin/center/${centerId}/subscription/${subscriptionId}`,
          truncate: true
        }
      ];
    }
  },
  watch: {},
  async mounted() {
    this.pageLoading = true;
    const centerId = Number(this.$route.params.center_id);
    const subscriptionId = Number(this.$route.params.id);
    if (!this.center) await this.getCenter({ id: centerId });
    await this.getSubscription({ center_id: centerId, id: subscriptionId });
    this.pageLoading = false;
  },
  methods: {
    ...mapActions([
      "getCenter",
      "getSubscription",
      "updateSubscription",
      "cancelSubscription"
    ]),
    displayDate,
    displayPrice,
    priceFormats(price = 0) {
      const settings = this.subscription.currency_settings;
      return this.displayPrice({ settings, price });
    },
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    async onCancel() {
      const params = {
        center_id: this.center.id,
        id: this.subscription.id,
        subscription: {
          cancellation_reason: this.cancellationReason.model || null
        }
      };

      this.submitLoading = true;
      const { success } = await this.cancelSubscription(params);
      this.submitLoading = false;
      if (!success) return;
      this.cancelOpened = false;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/superAdmin/center/subscription/Subscription.vue